import React from 'react';
import axios from 'axios';
import { Instagram, ExternalLink } from 'lucide-react';
import Reveal from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (!u ? '' : u.startsWith('http') ? u : `${BACKEND}${u}`);

const PostCard = ({ post }) => (
  <a
    href={post.permalink}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`instagram-post-${post.id}`}
    className="group relative block aspect-square rounded-2xl overflow-hidden bg-amber-50 border border-amber-100 hover-lift"
  >
    <img
      src={absUrl(post.media_url)}
      alt={post.caption || 'Publicação do Instagram HM Talent'}
      loading="lazy"
      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
      <div className="text-white">
        <Instagram className="w-5 h-5 mb-1.5" />
        {post.caption ? (
          <p className="text-xs leading-snug line-clamp-2">{post.caption}</p>
        ) : (
          <p className="text-xs leading-snug inline-flex items-center gap-1">Ver no Instagram <ExternalLink className="w-3 h-3" /></p>
        )}
      </div>
    </div>
  </a>
);

const InstagramFeed = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/instagram/feed`)
      .then((res) => mounted && setData(res.data))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const profileUrl = data?.profile_url || 'https://instagram.com/hmtalentrh';
  const username = data?.username || 'hmtalentrh';
  const posts = data?.posts || [];

  // Sem publicações cadastradas e nada carregando → não exibe a grade (evita estado vazio).
  const hasPosts = posts.length > 0;

  return (
    <section className="section-padding bg-white" data-testid="instagram-feed-section">
      <div className="container-hm">
        <Reveal className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm uppercase tracking-widest text-hm-orange font-bold mb-2">Redes Sociais</p>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900">Acompanhe a HM Talent</h2>
          <p className="mt-4 text-lg text-slate-700 leading-relaxed">
            Vagas em primeira mão, dicas de carreira e os bastidores do recrutamento ágil. Siga{' '}
            <span className="font-semibold text-hm-orange">@{username}</span> no Instagram.
          </p>
        </Reveal>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-amber-50 animate-pulse border border-amber-100" />
            ))}
          </div>
        ) : hasPosts ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6" data-testid="instagram-grid">
            {posts.map((post, i) => (
              <Reveal key={post.id} variant="scale" delay={(i % 3) * 80}>
                <PostCard post={post} />
              </Reveal>
            ))}
          </div>
        ) : null}

        <div className="mt-10 text-center">
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-button"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full font-bold text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #f58529 0%, #dd2a7b 50%, #8134af 100%)' }}
          >
            <Instagram className="w-5 h-5" /> Seguir @{username}
          </a>
        </div>
      </div>
    </section>
  );
};

export default InstagramFeed;
